import { supabase } from "@/lib/supabase";
import { isSupabaseConfigured } from "@/lib/env";
import { invokeFunction } from "@/lib/api";
import { seedJobs, type Job } from "@/content/jobs";
import type { Department } from "@/features/auth/types";

/**
 * Jobs data access. Staff edit postings in the portal; the public careers page
 * reads them. Two backends, same async API:
 *
 *  - Supabase (production): the `jobs` table is the source of truth, with RLS
 *    allowing public read of published rows and internal-only writes.
 *  - Local fallback (preview mode, no Supabase yet): persists to localStorage so
 *    the feature is fully usable in the browser. NOTE: localStorage is per-
 *    browser - edits are only visible on the device that made them until
 *    Supabase is connected. The DemoBanner makes this clear in the UI.
 */

const LS_KEY = "bb_jobs";
const LS_SEED_VERSION_KEY = "bb_jobs_seed_version";
/** Bump this whenever a NEW posting is added to seedJobs so existing browsers
 *  (whose localStorage was seeded earlier) pick it up on next load. Staff
 *  deletions are respected between bumps. */
const SEED_VERSION = 3;

// ---------------------------------------------------------------------------
// Supabase row <-> Job mapping (snake_case columns)
// ---------------------------------------------------------------------------
interface JobRow {
  slug: string;
  title: string;
  apply_type: Job["applyType"];
  employment: string;
  location: string;
  home_time: string;
  pay: string;
  summary: string;
  requirements: string[];
  highlights: string[];
  department: Department | null;
  published: boolean;
  sort_order: number;
}

function rowToJob(r: JobRow): Job {
  return {
    slug: r.slug,
    title: r.title,
    applyType: r.apply_type,
    employment: r.employment,
    location: r.location,
    homeTime: r.home_time,
    pay: r.pay,
    summary: r.summary,
    requirements: r.requirements ?? [],
    highlights: r.highlights ?? [],
    department: r.department ?? null,
    published: r.published,
    order: r.sort_order,
  };
}

const supabaseReady = () => isSupabaseConfigured && !!supabase;

// ---------------------------------------------------------------------------
// Local (localStorage) fallback
// ---------------------------------------------------------------------------
function localReadAll(): Job[] {
  let jobs: Job[] | null = null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) jobs = JSON.parse(raw) as Job[];
  } catch {
    /* ignore malformed cache */
  }

  // First run: seed from the static defaults.
  if (!jobs) {
    localStorage.setItem(LS_KEY, JSON.stringify(seedJobs));
    localStorage.setItem(LS_SEED_VERSION_KEY, String(SEED_VERSION));
    return [...seedJobs];
  }

  // Cache exists but the seed list grew since it was written: merge in any
  // seed postings the cache has never seen (one-time per SEED_VERSION bump,
  // so staff deletions aren't resurrected on every load).
  const applied = Number(localStorage.getItem(LS_SEED_VERSION_KEY) ?? "1");
  if (applied < SEED_VERSION) {
    const known = new Set(jobs.map((j) => j.slug));
    const additions = seedJobs.filter((s) => !known.has(s.slug));
    if (additions.length > 0) {
      jobs = [...jobs, ...additions];
      localStorage.setItem(LS_KEY, JSON.stringify(jobs));
    }
    localStorage.setItem(LS_SEED_VERSION_KEY, String(SEED_VERSION));
  }

  const normalized = jobs.map((job) =>
    job.applyType === "driver" || job.applyType === "carrier"
      ? { ...job, department: "safety" as const }
      : job,
  );
  if (normalized.some((job, idx) => job.department !== jobs![idx].department)) {
    jobs = normalized;
    localStorage.setItem(LS_KEY, JSON.stringify(jobs));
  }

  return jobs;
}

function localWriteAll(jobs: Job[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(jobs));
}

const bySortThenTitle = (a: Job, b: Job) => a.order - b.order || a.title.localeCompare(b.title);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export async function listAllJobs(): Promise<Job[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!.from("jobs").select("*").order("sort_order");
    if (error) throw error;
    return (data as JobRow[]).map(rowToJob);
  }
  return localReadAll().sort(bySortThenTitle);
}

export async function listPublishedJobs(): Promise<Job[]> {
  const all = await listAllJobs();
  return all.filter((j) => j.published).sort(bySortThenTitle);
}

export async function getJob(slug: string): Promise<Job | undefined> {
  if (!slug) return undefined;
  const all = await listAllJobs();
  return all.find((j) => j.slug === slug);
}

export async function saveJob(job: Job): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("jobs-admin", {
      action: "save",
      slug: job.slug,
      title: job.title,
      applyType: job.applyType,
      employment: job.employment,
      location: job.location,
      homeTime: job.homeTime,
      pay: job.pay,
      summary: job.summary,
      requirements: job.requirements,
      highlights: job.highlights,
      department: job.department,
      published: job.published,
      order: job.order,
    });
    return;
  }
  const all = localReadAll();
  const idx = all.findIndex((j) => j.slug === job.slug);
  if (idx >= 0) all[idx] = job;
  else all.push(job);
  localWriteAll(all);
}

export async function deleteJob(slug: string): Promise<void> {
  if (supabaseReady()) {
    await invokeFunction("jobs-admin", { action: "delete", slug });
    return;
  }
  localWriteAll(localReadAll().filter((j) => j.slug !== slug));
}

/** Turn a title into a URL-safe slug. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
